export function computeGlobalColumnOrderFromObjects(objects: Array<Record<string, unknown>>): string[] {
  const unionOrder: string[] = [];
  const firstSeenIndex = new Map<string, number>();

  const recordKey = (key: string) => {
    if (firstSeenIndex.has(key)) return;
    firstSeenIndex.set(key, unionOrder.length);
    unionOrder.push(key);
  };

  for (const obj of objects) {
    for (const key of Object.keys(obj ?? {})) {
      recordKey(key);
    }
  }

  // Build precedence constraints from all rows.
  // If a row contains keys [k1,k2,k3], we add edges k1→k2 and k2→k3.
  const adjacency = new Map<string, Set<string>>();
  const indegree = new Map<string, number>();

  const ensureNode = (key: string) => {
    if (!adjacency.has(key)) adjacency.set(key, new Set());
    if (!indegree.has(key)) indegree.set(key, 0);
  };

  for (const key of unionOrder) ensureNode(key);

  for (const obj of objects) {
    const keys = Object.keys(obj ?? {});
    for (const key of keys) ensureNode(key);

    for (let i = 0; i < keys.length - 1; i++) {
      const from = keys[i];
      const to = keys[i + 1];
      if (!from || !to || from === to) continue;

      const edges = adjacency.get(from);
      if (!edges) continue;
      if (edges.has(to)) continue;

      edges.add(to);
      indegree.set(to, (indegree.get(to) ?? 0) + 1);
    }
  }

  // Kahn's algorithm with stable tie-breaker:
  // When multiple keys are available, prefer the earliest-seen key.
  const ready: string[] = [];
  for (const [key, degree] of indegree.entries()) {
    if (degree === 0) ready.push(key);
  }

  const byFirstSeen = (a: string, b: string) => (firstSeenIndex.get(a) ?? 0) - (firstSeenIndex.get(b) ?? 0);
  ready.sort(byFirstSeen);

  const result: string[] = [];

  const insertReadySorted = (key: string) => {
    // Small N; simple insertion keeps determinism.
    let lo = 0;
    let hi = ready.length;
    const keyRank = firstSeenIndex.get(key) ?? 0;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      const midRank = firstSeenIndex.get(ready[mid]) ?? 0;
      if (midRank <= keyRank) lo = mid + 1;
      else hi = mid;
    }
    ready.splice(lo, 0, key);
  };

  while (ready.length > 0) {
    const cur = ready.shift()!;
    result.push(cur);

    const outs = adjacency.get(cur);
    if (!outs) continue;

    for (const next of outs) {
      const nextDeg = (indegree.get(next) ?? 0) - 1;
      indegree.set(next, nextDeg);
      if (nextDeg === 0) insertReadySorted(next);
    }
  }

  // Cycles are possible if different rows imply contradictory ordering.
  // In that case, fall back to a deterministic union order.
  if (result.length !== indegree.size) {
    return unionOrder;
  }

  return result;
}
